import {getTestProps} from '@benchling/js-test-utils';
import {Box, Loader, Center, Button, Group, Flex} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import DOMPurify from 'dompurify';
import React, {useEffect, useRef} from 'react';

import {getChemicalFileContent} from 'chemistry/ChemicalFileUtil';
import {ChemicalFileFormat} from 'chemistry/ChemistryConstants';
import {generateReactionImage} from 'chemistry/ChemistryDAO';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {StructureWithFormat} from 'notebook/integrations/chemistry/ReactionSketcherModal';
import InlineBanner, {InlineBannerType, InlineBannerActionType} from 'ui-library/banners/InlineBanner';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import useResizeSVG from 'ui-library/hooks/useResizeSVG';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {withLoadingScreen} from 'util/Promises';

interface MarvinReactionData {
  base64SVG: string;
  marvinStructureContent: string;
}

async function fetchMarvinContentAndPreview(
  externalFileId: string,
  externalFileUrl: string,
  entryId: string
): Promise<MarvinReactionData | null> {
  const signedUrl = await performSignedFileQuery(externalFileId, externalFileUrl, entryId);
  if (signedUrl != null) {
    const response = await fetch(signedUrl);
    if (response.ok) {
      // Note: could port this to use chemicalStructureFromFile
      const marvinStructureContent = await response.text();
      const {base64SVG} = await generateReactionImage(marvinStructureContent);
      return {base64SVG, marvinStructureContent};
    } else {
      throw new Error('Failed to fetch Marvin file content');
    }
  }
  return null;
}

export default function ReactionSchemeContent({
  externalFileId,
  externalFileUrl,
  entryId,
  openSketcherModalWithStructureAndFormat,
}: {
  externalFileId: string;
  externalFileUrl: string;
  entryId: string;
  openSketcherModalWithStructureAndFormat: (structureWithFormat: StructureWithFormat) => void;
}): JSX.Element {
  const reactionSVGRef = useResizeSVG<HTMLDivElement>(css(styles.reactionSVG));
  const {currentState: marvinContentAndPreviewAsyncState, makeCall: fetchMarvinContentAndPreviewAsync} =
    useAsyncState({makeCall: fetchMarvinContentAndPreview});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMarvinContentAndPreviewAsync(externalFileId, externalFileUrl, entryId);
  }, [externalFileId, externalFileUrl, entryId, fetchMarvinContentAndPreviewAsync]);

  const marvinContent =
    marvinContentAndPreviewAsyncState.status === AsyncStatus.SUCCESS &&
    marvinContentAndPreviewAsyncState.data != null
      ? marvinContentAndPreviewAsyncState.data
      : null;

  async function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = e.target.files;
    if (!files || files.length !== 1) {
      return;
    }
    try {
      // TODO(BNCH-140638): avoid hard-coding to CDX format and instead use file extension
      // NOTE: This logic is duplicated in EmptySchemePlaceholder - can consolidate once we
      // make the format detection more sophisticated
      const fileContent = await withLoadingScreen(getChemicalFileContent(ChemicalFileFormat.CDX, files[0]));
      openSketcherModalWithStructureAndFormat({
        structure: fileContent,
        format: MarvinChemicalFormat.CDX,
      });
    } finally {
      // Reset the input value to empty string to allow the same file to be selected again.
      e.target.value = '';
    }
  }

  if (marvinContentAndPreviewAsyncState.status === AsyncStatus.LOADING) {
    return (
      <Center h="100%">
        <Loader size="xl" />
      </Center>
    );
  } else if (marvinContent != null) {
    return (
      <Flex direction="column" w="100%" h="100%" gap="xs">
        <Group position="right">
          {/* Note that the Mantine dropzone uses the File System API through react-dropzone when possible instead of an input element */}
          <input
            {...getTestProps({component: 'ReactionSchemeContent', element: 'fileInput'})}
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            style={{display: 'none'}}
            multiple={false}
          />
          <Button
            variant="subtle"
            leftIcon={<BenchlingIcon icon="molecule" />}
            size="sm"
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            Replace
          </Button>
          <Button
            variant="outline"
            leftIcon={<BenchlingIcon icon="edit" />}
            size="sm"
            onClick={() => {
              openSketcherModalWithStructureAndFormat({
                structure: marvinContent.marvinStructureContent,
                format: MarvinChemicalFormat.MRV,
              });
            }}
          >
            Edit
          </Button>
        </Group>
        <Box
          ref={reactionSVGRef}
          role="img"
          aria-label="Reaction diagram"
          w="100%"
          h="100%"
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          // oxlint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(atob(marvinContent.base64SVG)),
          }}
        />
      </Flex>
    );
  } else {
    // TODO: Use design-approved error state UI
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Failed to load reaction scheme"
        action={{
          actionType: InlineBannerActionType.CUSTOM,
          actionText: 'Refresh',
          rightAlign: true,
          actionOnClick: () => {
            fetchMarvinContentAndPreviewAsync(externalFileId, externalFileUrl, entryId);
          },
        }}
      />
    );
  }
}

const styles = StyleSheet.create({
  // Ensure SVG scales proportionally within the container. useResizeSVG expects a single className arg so
  // define this style but prefer Mantine props otherwise.
  reactionSVG: {
    maxWidth: '100%',
    maxHeight: '100%',
  },
});
